import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ChargingStationService } from '@operate/charging-infrastructure-detail/services/charging-station.service';
import {
  SearchCategoryEnum,
  SearchResult,
} from '@shared/components/header/quick-access/data/quick-access.model';
import { QuickSearchBaseComponent } from '@shared/components/header/quick-access/quick-search-base.component';
import { QuickAccessService } from '@shared/components/header/quick-access/services/quick-access.service';
import { TopNotificationService } from '@shared/services/notification/top-notification.service';
import { IconColor } from '@widgets/eop-icon/eop-icon.component';
import { take } from 'rxjs';
import { ChargePortNameUuid } from '@operate/charging-infrastructure-detail/model/charging-infrastructure-detail.model';

export interface StationSearchResult {
  chargingStationId: string;
  chargingStationName: string;
  chargePorts: ChargePortNameUuid[];
  selectedChargePort?: string;
}

@Component({
  selector: 'eop-station-search-field',
  templateUrl: './eop-station-search-field.component.html',
  styleUrl: './eop-station-search-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EopStationSearchFieldComponent extends QuickSearchBaseComponent {
  readonly IconColor = IconColor;

  @ViewChild('stationNameInput', { static: true }) stationNameRef!: ElementRef<HTMLInputElement>;
  @ViewChild('searchInput') searchInputElement: ElementRef<HTMLInputElement>;

  @Input() dataTestId = 'search-for-station-or-port';
  @Input() stationName = '';

  @Output() itemSelected: EventEmitter<StationSearchResult> = new EventEmitter();

  protected topNotificationService: TopNotificationService;
  protected quickAccessService: QuickAccessService;
  protected cdr: ChangeDetectorRef;
  private readonly chargingStationService = inject(ChargingStationService);

  constructor() {
    const topNotificationService = inject(TopNotificationService);
    const quickAccessService = inject(QuickAccessService);
    const cdr = inject(ChangeDetectorRef);

    super(quickAccessService, topNotificationService, cdr);

    this.topNotificationService = topNotificationService;
    this.quickAccessService = quickAccessService;
    this.cdr = cdr;
  }

  originPopover(): ElementRef<HTMLElement> {
    return this.stationNameRef;
  }

  searchInput(): ElementRef<HTMLInputElement> {
    return this.searchInputElement;
  }

  selected(element: SearchResult) {
    this.searchInProgress = true;
    this.chargingStationService
      .stationInformation(element.stationUuid)
      .pipe(take(1))
      .subscribe({
        next: stationInfo => {
          const result: StationSearchResult = {
            chargingStationId: stationInfo.id,
            chargingStationName: stationInfo.name,
            selectedChargePort:
              element.category === SearchCategoryEnum.CHARGEPOINT ? element.value : null,
            chargePorts: stationInfo.evses.map(evse => {
              return { chargePortName: evse.name, chargePortUuid: evse.id };
            }),
          };
          this.searchInProgress = false;
          this.stationName = stationInfo.name;
          this.itemSelected.emit(result);
          this.closeDialog();
        },
        error: error => {
          this.searchInProgress = false;
          this.topNotificationService.showErrorInTopNotification(error);
        },
      });
  }

  protected resetSearchInputOnRouteChange(): void {
    // not needed
  }
}
